import { Delete, Edit, PictureAsPdf, Visibility } from '@mui/icons-material';
import { status } from 'adminConstants/investorReporting';
import { InvestorReportingType } from 'further-types/investor-reporting';
import {
  createCustomUpdateViewRoute,
  createCustomUpdateEditRoute,
  createDeploymentUpdateViewRoute,
  createExitStatementPdfDownloadRoute,
  createExitStatementViewRoute,
  createExitStatementAddRoute,
  createInvestorReportingEditRoute,
  createInvestorReportingPdfDownloadRoute,
  createInvestorReportingViewRoute,
  createUpdateExitStatementRoute,
} from 'adminConstants/routes';
import { TableActions } from 'components/Table';
import { useDisclosure } from 'further-ui/hooks';
import React from 'react';

type Props = {
  investorReport: {
    _id: string;
    updateType: string;
    status: string;
    viewable: boolean;
    affectedCompanies?: Array<{ id: string; name: string }>;
    exitId?: string;
    exitAmendmentId?: string;
  };
  canEditPeriodic: boolean;
  canEditCustom: boolean;
  canEditExitStatement: boolean;
  canDeletePeriodic: boolean;
  canDeleteCustom: boolean;
  canDeleteExitStatement: boolean;
  deleteDisclosure: ReturnType<typeof useDisclosure>;
  canPublishPeriodic: boolean;
  canPublishCustom: boolean;
};

const ActionTools: React.FC<Props> = ({
  investorReport,
  canEditPeriodic,
  canEditCustom,
  canEditExitStatement,
  canDeletePeriodic,
  canDeleteCustom,
  canDeleteExitStatement,
  canPublishPeriodic,
  canPublishCustom,
  deleteDisclosure,
}) => {
  const isCustomUpdate =
    investorReport.updateType === InvestorReportingType.CustomUpdate;
  const isDeploymentUpdate =
    investorReport.updateType === InvestorReportingType.DeploymentUpdate;
  const isPeriodicUpdate =
    investorReport.updateType === InvestorReportingType.Periodic;
  const isExitStatement =
    investorReport.updateType === InvestorReportingType.ExitStatement;
  const isSubmitted = investorReport.status === status.SUBMITTED;

  const isDraftExitStatement =
    isExitStatement && investorReport.status === status.DRAFT;
  const isDraftPeriodicUpdate =
    isPeriodicUpdate && investorReport.status === status.DRAFT;
  const isDraftCustomUpdate =
    isCustomUpdate && investorReport.status === status.DRAFT;

  const showViewButton =
    investorReport.viewable ||
    isCustomUpdate ||
    isDeploymentUpdate ||
    (isExitStatement && !isDraftExitStatement);
  const viewButtonLink = isCustomUpdate
    ? createCustomUpdateViewRoute(investorReport._id)
    : isDeploymentUpdate
    ? createDeploymentUpdateViewRoute(investorReport._id)
    : isExitStatement
    ? createExitStatementViewRoute(investorReport._id)
    : createInvestorReportingViewRoute(investorReport._id);

  const editButtonLink = isDraftExitStatement
    ? createExitStatementAddRoute(
        investorReport.exitId || '',
        investorReport.exitAmendmentId || undefined,
      )
    : isExitStatement
    ? createUpdateExitStatementRoute(investorReport._id)
    : isCustomUpdate
    ? createCustomUpdateEditRoute(investorReport._id)
    : createInvestorReportingEditRoute(investorReport._id);

  const canUpdate = isPeriodicUpdate
    ? canEditPeriodic && (isSubmitted ? canPublishPeriodic : true)
    : isCustomUpdate
    ? canEditCustom && (isSubmitted ? canPublishCustom : true)
    : isExitStatement
    ? canEditExitStatement
    : false;
  const canDelete = isPeriodicUpdate
    ? canDeletePeriodic
    : isCustomUpdate
    ? canDeleteCustom
    : isExitStatement
    ? canDeleteExitStatement
    : false;
  const showEditButton = canUpdate && !isDeploymentUpdate;
  const showDeleteButton = canDelete && !isDeploymentUpdate;

  return (
    <TableActions
      showAsDropdown
      actions={[
        {
          label: 'View',
          icon: Visibility,
          color: 'primary',
          link: viewButtonLink,
          visible: showViewButton,
        },
        {
          label:
            isDraftExitStatement || isDraftPeriodicUpdate || isDraftCustomUpdate
              ? 'Edit draft'
              : 'Edit',
          icon: Edit,
          color: 'primary',
          link: editButtonLink,
          visible: showEditButton,
        },
        {
          label: 'Delete',
          icon: Delete,
          color: 'error',
          onClick: () =>
            deleteDisclosure.stageAction({
              id: investorReport._id,
              isExitStatement,
              isCustomUpdate,
              isPeriodicUpdate,
              affectedCompanies: investorReport.affectedCompanies,
            }),
          visible: showDeleteButton,
        },
        {
          label: 'Download PDFs',
          icon: PictureAsPdf,
          color: 'primary',
          link: createInvestorReportingPdfDownloadRoute(investorReport._id),
          visible: isPeriodicUpdate && isSubmitted,
        },
        {
          label: 'Download PDFs',
          icon: PictureAsPdf,
          color: 'primary',
          link: createExitStatementPdfDownloadRoute(investorReport._id),
          visible: isExitStatement,
        },
      ]}
    />
  );
};

export default ActionTools;
